import { themeVariables } from '@noths/polaris-design-system-abstract-theme';
import { css } from 'styled-components';

const { colors, spacing, typography } = themeVariables;

/* Offer and sale differ only in a colour and the order of two prices, so both
   live in one static class and swap a CSS variable through a data attribute.
   That keeps a single injected rule however many cards a listing renders, rather
   than one class per emphasis. */
const emphasis = css`
  --price-emphasis: var(${colors.content.default});

  &[data-emphasis='offer'] {
    --price-emphasis: var(${colors.content.positive});
  }

  &[data-emphasis='sale'] {
    --price-emphasis: var(${colors.content.negative});
  }
`;

/* Wraps as relief rather than as a layout the design asks for. The row holds a
   sale tag, two prices that cannot break inside themselves and a rating, none of
   which shrink, and on the narrowest card the carousel renders that comes to more
   than the card is wide. Without somewhere to give, the row's min-content becomes
   the card's, and the card either clips it or grows past the width it was given.

   So the rating drops beneath the prices, and only at a width where it would not
   have fitted beside them. `space-between` leaves it at the start of its own line,
   which lines it up with the partner name, title and saving above and below it.

   The gaps are split because the two directions want different values: `xs`
   between the prices and the rating, as before, and the `xxs` of the details
   column between the two lines, so a wrapped rating keeps that rhythm. */
export const priceRow = css`
  ${emphasis}

  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  column-gap: var(${spacing.xs});
  row-gap: var(${spacing.xxs});
  margin: 0;
  font-family: var(${typography.body.fontFamily});
  font-size: var(${typography.body.sizes.md.fontSize});
  line-height: var(${typography.body.sizes.md.lineHeight});
  color: var(${colors.content.default});
`;

export const prices = css`
  display: flex;
  align-items: baseline;
  gap: var(${spacing.xxs});
  min-width: 0;
`;

/* The tag only appears alongside a sale, so it inherits the emphasis colour
   rather than naming the negative token again. */
export const saleIcon = css`
  display: inline-flex;
  flex-shrink: 0;
  align-self: center;
  line-height: 0;
  color: var(--price-emphasis);
`;

/* The theme has one body weight and no bold token, so bold is stated as a literal
   here as it is in the core Button. Worth a token if a third component needs it. */
export const price = css`
  font-weight: 700;
  color: var(--price-emphasis);
  white-space: nowrap;
`;

/* The PDP leads with the price, so it takes the heading scale there. The original
   price and the saving stay at the body scale in both, so only this one rule
   varies. */
export const priceLarge = css`
  font-family: var(${typography.heading.fontFamily});
  font-size: var(${typography.heading.sizes.xs.fontSize});
  line-height: var(${typography.heading.sizes.xs.lineHeight});
`;

/* "From", when the product's options add cost. Sits at the body scale whatever
   the price does, so it reads as a qualifier rather than part of the number. */
export const from = css`
  margin: 0;
  font-family: var(${typography.body.fontFamily});
  font-size: var(${typography.body.sizes.sm.fontSize});
  line-height: var(${typography.body.sizes.sm.lineHeight});
  color: var(${colors.content.default});
`;

export const originalPrice = css`
  font-size: var(${typography.body.sizes.sm.fontSize});
  color: var(${colors.content.secondary});
  white-space: nowrap;
  /* Stated rather than left to the user agent's default for <s>, so the
     strike-through is part of the component's contract. */
  text-decoration: line-through;
`;

export const discount = css`
  ${emphasis}

  margin: 0;
  font-family: var(${typography.body.fontFamily});
  font-size: var(${typography.body.sizes.sm.fontSize});
  line-height: var(${typography.body.sizes.sm.lineHeight});
  color: var(--price-emphasis);
`;
